import { Box, Button, Divider, Typography } from "@mui/material";
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import useCanRespondToDecisions from "../../../../../hooks/useCanRespondToDecisions";
import {
  acceptFutureDealOption,
  clearRuleEngineResponse,
  respondToRuleEngineDecision,
  storeAnalyticsSelector,
  updateDealUploadStatus,
  upsertProductComment,
} from "../../../../../redux/slices/store_analytics";
import { trackEvent } from "../../../../../utils/analytics";
import { AGENT_INFO } from "../../constants";

import DaveDealAction from "./DaveDealAction";

// Helper to format date to display format
const formatDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const ReginaDecisionCard = ({
  asin,
  sku,
  executionId,
  store,
  marketplace,
  reginaData,
  onRemove,
  onDealRejected,
}) => {
  const dispatch = useDispatch();
  const {
    saveRuleEngineResponseLoading: saveRuleEngineResponseLoadingMap,
    saveRuleEngineResponseStatus,
  } = useSelector(storeAnalyticsSelector);
  const agentName = (reginaData?.decision?.agent || "regina").toLowerCase();
  const statusKey = `${executionId}_${asin}_${agentName}`;
  const saveRuleEngineResponseLoading =
    !!saveRuleEngineResponseLoadingMap[statusKey];
  const [isReviewed, setIsReviewed] = useState(false);
  const [showDealConfirmation, setShowDealConfirmation] = useState(false);
  const [dealAccepted, setDealAccepted] = useState(false);
  const [showDealRejectReasonInput, setShowDealRejectReasonInput] =
    useState(false);
  const [dealRejectReason, setDealRejectReason] = useState("");

  // Extract data from reginaData
  const features = reginaData?.features || {};
  const decision = reginaData?.decision || {};
  const deal = reginaData?.deal || null;
  const dealAvailable = reginaData?.deal_available || false;
  // Dave is the one executing the deal — the card art should reflect
  // whoever is acting, not just whoever sensed it (Regina).
  const showDaveArt = dealAvailable && !!deal;

  const weightedRankBefore = features.WeightedRankBefore7Days || 0;
  const weightedRankAfter = features.WeightedRankAfter7Days || 0;
  const deteriorationDelta = features.DeteriorationDelta || 0;
  const thresholdUsed = features.ThresholdUsed || 10;

  const action = decision.action || "Ranking Deteriorated";
  const reasoning = decision.reasoning || "";

  // Extract date from reginaData.created_at
  const analysisDate = reginaData?.created_at?.split("T")[0] || "";

  // For documenting deal accept/reject as a product comment (same 30-day
  // window RuleEngineDecision uses for the equivalent Iris comments).
  const today = new Date();
  const commentStartDate = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
    .toISOString()
    .split("T")[0];
  const commentEndDate = today.toISOString().split("T")[0];
  const cellForCommentDocumentation = today.toISOString().slice(5, 10);

  // Calculate relative time string
  const getRelativeTime = (dateString) => {
    if (!dateString) return "";
    const analysisDateObj = new Date(dateString);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    analysisDateObj.setHours(0, 0, 0, 0);

    const diffTime = today - analysisDateObj;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return "Detected Today";
    if (diffDays === 1) return "Detected Yesterday";
    if (diffDays > 1 && diffDays <= 4) return `Detected ${diffDays} days ago`;
    return `Detected ${formatDate(dateString)}`;
  };

  const relativeTimeText = getRelativeTime(analysisDate);

  const userAllowedToRespond = useCanRespondToDecisions(store);

  const handleMarkAsReviewed = async () => {
    trackEvent("regina_marked_as_reviewed", {
      asin,
      store,
      execution_id: executionId,
    });
    const payload = {
      asin: asin,
      sku: sku,
      store: store,
      execution_id: executionId,
      status_key: statusKey,
      marketplace: marketplace,
      user_response: "Dismiss",
      decision_type: "regina_analysis",
      ...(dealAvailable && deal ? { reject_reason: dealRejectReason } : {}),
    };

    const result = await dispatch(respondToRuleEngineDecision(payload));
    if (result.success) {
      setIsReviewed(true);

      // When a deal was on offer, Dismiss is Regina's equivalent of Iris's
      // deal-reject — document it as Dave's rejection, same as that path.
      if (dealAvailable && deal) {
        setShowDealRejectReasonInput(false);
        onDealRejected?.();
        const commentObj = {
          asin: asin,
          store: store,
          cell: cellForCommentDocumentation,
          text: `Handled by ${AGENT_INFO.dave.name} — Rejected Best Deal ($${deal.deal_price} offer): ${dealRejectReason}`,
          user: "Agent " + agentName + " ",
        };
        dispatch(
          upsertProductComment(
            commentObj,
            store,
            commentStartDate,
            commentEndDate
          )
        );
      }
    }
  };

  const confirmDealAccept = async () => {
    const dealId = deal?.deal_id || deal?.id;
    trackEvent("regina_deal_accepted", { asin, store, deal_id: dealId });
    const payload = {
      asin: asin,
      dealId: dealId,
      store,
      execution_id: executionId,
      status_key: statusKey,
      notes: `Accepted via Regina recommendation for ASIN ${asin}`,
    };
    const result = await dispatch(acceptFutureDealOption(payload));
    if (result.success) {
      setShowDealConfirmation(false);
      setDealAccepted(true);

      // Update deal upload status if batch_id is returned
      if (result.data?.batch_id && result.data?.upload_started) {
        dispatch(
          updateDealUploadStatus({
            asin,
            status: "pending",
            batchId: result.data.batch_id,
            dealAccepted: true,
          })
        );
      }

      const commentObj = {
        asin: asin,
        store: store,
        cell: cellForCommentDocumentation,
        text: (() => {
          const priceWas = deal.seller_price || reginaData?.selling_price;
          const pricePart = priceWas
            ? `Scheduled Best Deal: $${deal.deal_price} (was $${priceWas})`
            : `Scheduled Best Deal: $${deal.deal_price}`;
          const startDate = deal.adjusted_start_date || deal.start_date;
          const endDate = deal.adjusted_end_date || deal.end_date;
          const datePart =
            startDate && endDate ? ` from ${startDate} to ${endDate}` : "";
          return `Handled by ${AGENT_INFO.dave.name} — ${pricePart}${datePart}`;
        })(),
        user: "Agent " + agentName + " ",
      };
      dispatch(
        upsertProductComment(
          commentObj,
          store,
          commentStartDate,
          commentEndDate
        )
      );
    }
  };

  return (
    <Box
      sx={{
        p: 2,
        position: "relative",
        minHeight: "150px",
        minWidth: "380px",
        width: "100%",
      }}
    >
      {/* Agent image — Dave when he's the one executing a live deal, Regina otherwise */}
      <img
        src={showDaveArt ? AGENT_INFO.dave.avatar : "/assets/regina.png"}
        alt={showDaveArt ? AGENT_INFO.dave.name : "Regina"}
        style={{
          position: "absolute",
          bottom: -20,
          right: -31,
          width: 100,
          height: 100,
          opacity: 0.7,
        }}
      />

      {saveRuleEngineResponseLoading ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 2 }}>
          <div
            style={{
              width: 16,
              height: 16,
              border: "2px solid #e3f2fd",
              borderTop: "2px solid #1976d2",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
            }}
          />
          <Typography variant="body2" sx={{ color: "#1976d2" }}>
            Processing...
          </Typography>
          <style>
            {`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}
          </style>
        </Box>
      ) : isReviewed ||
        dealAccepted ||
        saveRuleEngineResponseStatus[statusKey]?.message ? (
        <Box>
          <Typography
            variant="body2"
            sx={{
              color: "#4caf50",
              fontWeight: 500,
              py: 1,
            }}
          >
            {dealAccepted
              ? "Deal scheduled successfully"
              : saveRuleEngineResponseStatus[statusKey]?.message ||
                "Alert marked as reviewed"}
          </Typography>
          {onRemove && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                trackEvent("regina_response_cleared", {
                  asin,
                  store,
                  execution_id: executionId,
                });
                dispatch(
                  clearRuleEngineResponse({
                    execution_id: executionId,
                    status_key: statusKey,
                  })
                );
                onRemove();
              }}
              sx={{
                mt: 1,
                color: "#666",
                borderColor: "#e0e0e0",
                textTransform: "none",
                fontWeight: 500,
                borderRadius: 3,
                "&:hover": {
                  borderColor: "#999",
                  backgroundColor: "transparent",
                },
              }}
            >
              Close
            </Button>
          )}
        </Box>
      ) : (
        <>
          {/* Header with action */}
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="subtitle2"
              sx={{ color: "#6b7280", fontSize: "0.875rem" }}
            >
              Triggered By{" "}
              <span style={{ color: AGENT_INFO.regina.color, fontWeight: 600 }}>
                {AGENT_INFO.regina.name}
              </span>
              {action ? `: ${action}` : ""}
            </Typography>
          </Box>

          {/* Reasoning text */}
          <Typography
            variant="body2"
            sx={{
              color: "#6b7280",
              lineHeight: 1.5,
              fontStyle: "italic",
              fontSize: "0.78rem",
              mb: 2,
            }}
          >
            {reasoning}
          </Typography>

          {/* Weighted Rank Section */}
          <Box sx={{ mb: 2, mt: 2, maxWidth: "85%", pr: 4, pl: 2, ml: 4 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-start",
                alignItems: "center",
                mb: 0.75,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  color: "#111827",
                  fontSize: "0.75rem",
                  ml: -2,
                }}
              >
                7-Day Weighted Rank
              </Typography>
            </Box>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "56px 8px auto",
                gridTemplateRows: "auto auto",
                alignItems: "start",
                columnGap: 4,
                rowGap: 0.25,
                width: "fit-content",
                ml: 0,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  gridColumn: 1,
                  gridRow: 1,
                  color: "#9e9e9e",
                  fontSize: "0.7rem",
                  lineHeight: 1,
                  pl: 0.5,
                }}
              >
                Before
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  gridColumn: 3,
                  gridRow: 1,
                  color: "#9e9e9e",
                  fontSize: "0.7rem",
                  lineHeight: 1,
                  textAlign: "left",
                }}
              >
                After
              </Typography>
              <Box
                sx={{
                  gridColumn: 1,
                  gridRow: 2,
                  minWidth: 52,
                  display: "flex",
                  alignItems: "center",
                  pl: 0.5,
                  mt: 1,
                }}
              >
                <Typography sx={{ color: "#6b7280", fontWeight: 500 }}>
                  {weightedRankBefore.toFixed(1)}
                </Typography>
              </Box>
              <Box
                sx={{
                  gridColumn: 2,
                  gridRow: 2,
                  width: 8,
                  display: "flex",
                  justifyContent: "flex-start",
                  ml: -4,
                  mt: 1.5,
                }}
              >
                <Typography
                  variant="h5"
                  sx={{ color: "#9ca3af", fontWeight: 700, lineHeight: 1 }}
                >
                  →
                </Typography>
              </Box>
              <Box
                sx={{
                  gridColumn: 3,
                  gridRow: 2,
                  textAlign: "left",
                  display: "flex",
                  alignItems: "flex-start",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "baseline",
                    gap: 0.5,
                  }}
                >
                  <Typography
                    component="span"
                    sx={{
                      color: "#374151",
                      fontWeight: 700,
                      fontSize: "1.25rem",
                    }}
                  >
                    {weightedRankAfter.toFixed(1)}
                  </Typography>
                  <Typography
                    component="span"
                    sx={{
                      color: "#f44336",
                      fontWeight: 700,
                      fontSize: "1.25rem",
                    }}
                  >
                    (+{deteriorationDelta.toFixed(1)})
                  </Typography>
                </Box>
              </Box>
            </Box>
            <Typography
              variant="caption"
              sx={{
                color: "#9e9e9e",
                fontSize: "0.7rem",
                mt: 0.5,
                display: "block",
                ml: -2,
              }}
            >
              Threshold: {thresholdUsed} spots
            </Typography>
          </Box>

          {/* Deal Section - Show if deal is available */}
          {dealAvailable && deal && (
            <>
              <Divider sx={{ my: 2 }} />
              <DaveDealAction
                deal={deal}
                currentPrice={deal.seller_price || reginaData.selling_price}
                acceptDisabled={
                  !userAllowedToRespond ||
                  saveRuleEngineResponseLoading ||
                  showDealConfirmation
                }
                rejectDisabled={
                  !userAllowedToRespond || saveRuleEngineResponseLoading
                }
                confirmDisabled={saveRuleEngineResponseLoading}
                rejectLoading={saveRuleEngineResponseLoading}
                showDealConfirmation={showDealConfirmation}
                onAcceptClick={() => setShowDealConfirmation(true)}
                onConfirmAccept={confirmDealAccept}
                onCancelConfirmation={() => setShowDealConfirmation(false)}
                onReject={() => setShowDealRejectReasonInput(true)}
                showRejectReasonInput={showDealRejectReasonInput}
                rejectReason={dealRejectReason}
                onRejectReasonChange={setDealRejectReason}
                onConfirmReject={handleMarkAsReviewed}
                onCancelReject={() => {
                  setShowDealRejectReasonInput(false);
                  setDealRejectReason("");
                }}
              />
            </>
          )}

          {/* Mark as Reviewed Button (when no deal available) */}
          {!dealAvailable && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                pr: 10,
                mt: 2,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Button
                  variant="outlined"
                  size="small"
                  disabled={
                    !userAllowedToRespond || saveRuleEngineResponseLoading
                  }
                  onClick={
                    userAllowedToRespond ? handleMarkAsReviewed : undefined
                  }
                  sx={{
                    backgroundColor: userAllowedToRespond ? "#fff" : "#f5f5f5",
                    color: userAllowedToRespond ? "#1976d2" : "#9e9e9e",
                    borderColor: userAllowedToRespond ? "#1976d2" : "#e0e0e0",
                    textTransform: "none",
                    fontWeight: 500,
                    boxShadow: "none",
                    borderRadius: 2,
                    px: 2,
                    "&:hover": userAllowedToRespond
                      ? {
                          backgroundColor: "#e3f2fd",
                          borderColor: "#1565c0",
                        }
                      : {},
                  }}
                >
                  Mark as Reviewed
                </Button>
                <Typography
                  variant="caption"
                  sx={{
                    color: "#999",
                    fontSize: "0.75rem",
                    fontStyle: "italic",
                  }}
                >
                  ({relativeTimeText})
                </Typography>
              </Box>
              <Box />
            </Box>
          )}

          {!userAllowedToRespond && (
            <Typography
              variant="body2"
              sx={{
                color: "red",
                fontStyle: "italic",
                mt: 1,
                textAlign: "center",
                fontSize: "0.75rem",
              }}
            >
              Only the store owner can respond.
            </Typography>
          )}
        </>
      )}
    </Box>
  );
};

export default ReginaDecisionCard;
